import { Box, Icons, iconSize, palette, Stack, Typography, fontStacks } from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { AuthErrorBanner } from './authFields';

// Seeded demo accounts. They share one password and are real accounts, so quick
// sign-in performs an actual login — cookies get set and the session is
// indistinguishable from typing credentials.
//
// Two seeds feed this list, and they are seeded at different moments:
//   • the officers come from gov-api's `demo-officers.ts`, upserted at boot when
//     GOV_SEED_DEMO_USERS=true;
//   • everyone below them comes from `tools/db/scoped-access-seed.ts`, written by
//     `pnpm db:seed` under the same flag.
// So a database that has only ever booted the API will light up the first group
// and fail on the rest — hence the error copy pointing at both.
//
// The seats and staff carry *no* `roles` column: their authority is the
// membership rows, which is exactly what these accounts exist to exercise. The
// label beside each one names the seat and the tenant it belongs to.
const DEMO_PASSWORD = 'govdemo1';

interface DevUser {
  name: string;
  email: string;
  role: string;
}

const DEV_GROUPS: Array<{ heading: string; users: readonly DevUser[] }> = [
  {
    heading: 'Enforcement officers',
    users: [
      {
        name: 'Avery Tremblay',
        email: 'avery.tremblay@municipality.ca',
        role: 'Enforcement Officer',
      },
      { name: 'Samuel Côté', email: 'samuel.cote@municipality.ca', role: 'Senior Officer' },
      { name: 'Priya Sharma', email: 'priya.sharma@municipality.ca', role: 'Supervisor' },
    ],
  },
  {
    heading: 'Municipal seats',
    users: [
      // Addressed by person, not by town. The seed renamed these when the seats
      // stopped being named after municipalities (see RENAMED_DEMO_ACCOUNTS in
      // scoped-access-seed.ts) — one account legitimately holds seats at two
      // towns, so `clerk.dieppe@` was never a name for a person. This list kept
      // the old addresses, so six of the seven rows posted an address that no
      // longer existed and the box blamed the database for it.
      { name: 'Daniel Okafor', email: 'daniel.okafor@municipality.ca', role: 'Clerk · Moncton' },
      {
        name: 'Hélène Boudreau',
        email: 'helene.boudreau@municipality.ca',
        role: 'Counsel · Moncton',
      },
      { name: 'Grace Wu', email: 'grace.wu@municipality.ca', role: 'Finance · Moncton' },
      // The Moncton supervisor seat attaches to Priya's officer account rather
      // than to a second copy of her, so this row is the same account as the
      // one above under Enforcement officers.
      {
        name: 'Priya Sharma',
        email: 'priya.sharma@municipality.ca',
        role: 'Supervisor · Moncton',
      },
      // One account, two seats — clerk and counsel at Dieppe.
      // Three seats, two towns — the case a single account-level role could
      // never have represented.
      {
        name: 'Sylvie Richard',
        email: 'sylvie.richard@municipality.ca',
        role: 'Clerk + counsel · Dieppe, clerk · Shediac',
      },
      // Supervisor, not clerk — the seed moved this seat and the label did not
      // follow, so signing in here looked like a clerk who had lost their
      // powers rather than a supervisor with different ones.
      { name: 'Emma Ward', email: 'emma.ward@municipality.ca', role: 'Supervisor · Shediac' },
      // Riverview has no contract, so this tenant is unreachable from any
      // contractor account — sign in here to see the other side of isolation.
      { name: 'Nadia Haddad', email: 'nadia.haddad@municipality.ca', role: 'Clerk · Riverview' },
    ],
  },
  {
    heading: 'Contractor staff',
    users: [
      { name: 'Jordan Vautour', email: 'admin@mes-nb.ca', role: 'Company admin · MES' },
      { name: 'Chantal Doucet', email: 'supervisor@mes-nb.ca', role: 'Company supervisor · MES' },
      { name: 'Liam Gallant', email: 'officer1@mes-nb.ca', role: 'Officer · MES' },
      { name: 'Fatima Nasser', email: 'officer2@mes-nb.ca', role: 'Officer · MES' },
      // Employed, never appointed: signing in here should show no files.
      { name: 'Owen Léger', email: 'newhire@mes-nb.ca', role: 'Officer, unappointed · MES' },
      { name: 'Rebecca Toner', email: 'admin@atlanticbylaw.ca', role: 'Company admin · Atlantic' },
    ],
  },
];

/**
 * Development-only quick sign-in panel. Renders nothing in production builds, so
 * it's safe to leave mounted. Requires gov-api running with the seeded officers,
 * and `pnpm db:seed` for the seats and contractor staff.
 */
export function DevUsers() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState<'unseeded' | 'unreachable' | null>(null);

  if (!import.meta.env.DEV) return null;

  const signInAs = async (email: string) => {
    setPending(email);
    setFailed(null);
    try {
      await login(email, DEMO_PASSWORD);
      navigate('/');
    } catch (err) {
      // Two very different failures used to read as one. A 401 does mean the
      // accounts were never seeded; no response at all means gov-api is not
      // running, and telling someone to re-seed their database then sends them
      // to fix a table that was never the problem.
      setFailed(isAxiosError(err) && err.response ? 'unseeded' : 'unreachable');
    } finally {
      setPending(null);
    }
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: 1.5,
        borderRadius: '10px',
        border: `1px dashed ${palette.border.lineStrong}`,
        backgroundColor: palette.background.parch,
      }}
    >
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 1, px: 0.5 }}>
        <Icons.Zap size={iconSize.xs} color={palette.text.muted} />
        <Typography
          sx={{
            fontFamily: fontStacks.display,
            fontWeight: 600,
            fontSize: 10.5,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: palette.text.muted,
          }}
        >
          {t('gov.login.devSignIn')}
        </Typography>
      </Stack>

      {/* Sixteen accounts would push the sign-in form off the page, so the list
          scrolls inside a fixed height. */}
      <Box sx={{ maxHeight: 264, overflowY: 'auto', pr: 0.25 }}>
        <Stack spacing={1}>
          {DEV_GROUPS.map((group) => (
            <Stack key={group.heading} spacing={0.5}>
              <Typography
                sx={{
                  px: 1,
                  fontFamily: fontStacks.display,
                  fontWeight: 600,
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: palette.text.muted,
                }}
              >
                {group.heading}
              </Typography>
              {group.users.map((user) => (
                <Box
                  key={user.email}
                  component="button"
                  type="button"
                  disabled={pending !== null}
                  onClick={() => void signInAs(user.email)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                    textAlign: 'left',
                    px: 1,
                    py: 0.875,
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    transition: 'background-color 150ms ease-out',
                    '&:hover': { backgroundColor: palette.brand.accentTint },
                    '&:disabled': { cursor: 'default', opacity: pending === user.email ? 1 : 0.5 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontSize: 13.5, fontWeight: 600, color: palette.text.primary }}
                    >
                      {user.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: fontStacks.mono,
                        fontSize: 11.5,
                        color: palette.text.muted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={user.email}
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexShrink: 0 }}>
                    <Typography
                      sx={{ fontSize: 11.5, color: palette.text.secondary, whiteSpace: 'nowrap' }}
                    >
                      {pending === user.email ? t('gov.login.signingIn') : user.role}
                    </Typography>
                    <Icons.ChevronRight size={iconSize.sm} color={palette.text.muted} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          ))}
        </Stack>
      </Box>

      {failed && (
        <Box sx={{ mt: 1 }}>
          <AuthErrorBanner>{t(`gov.login.devSignInFailed.${failed}`)}</AuthErrorBanner>
        </Box>
      )}
    </Box>
  );
}
